import { useState, useEffect } from "react";
import {authenticationService} from "../jwt/_services/authentication.service";
import { isDemoMode, isBrowserSessionDemo } from "./demoConfig";
import { getMockResponse } from "./mockApi";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;
const PLATFORM_API_URL = process.env.REACT_APP_PLATFORM_API_URL_BASE;

function shouldFetchPassthroughRealApi(url) {
  if (!url) {
    return false;
  }
  const p = url.split("?")[0].replace(/\/+$/, "").toLowerCase();
  return p === "applications" || p.startsWith("applications/");
}

function useFetch(method, url, token, body, onChange = false, usePlatformApi = false, bypassDemoMock = false) {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const correct_url = usePlatformApi ? PLATFORM_API_URL + url : API_URL + url;
  let options;

  if (method === 'GET') {
    options = {
      'method': method,
      mode: 'cors',
      headers: {
        ...(usePlatformApi ? {} : { Authorization: `Bearer ${token}` }),
        "x-api-key": X_API_KEY,
        'Content-Type': 'application/json'
      }
    }
  } else {
    options = {
      'method': method,
      mode: 'cors',
      headers: {
        ...(usePlatformApi ? {} : { Authorization: `Bearer ${token}` }),
        "x-api-key": X_API_KEY,
        'Content-Type': 'application/json'
      },
      body: { body }
    }
  }

  useEffect(() => {
    let cancelled = false;

    const fetchUrl = async () => {
      if (!url) {
        if (!cancelled) {
          setData([]);
          setError(null);
          setLoading(false);
        }
        return false;
      }
      if (!cancelled) {
        setLoading(true);
      }
      if (!token && !usePlatformApi) {
        if (!cancelled) {
          setLoading(false);
        }
        return false;
      }
      // Demo mode: serve mock data only — never call the real tenant or platform API.
      if (
        !bypassDemoMock &&
        (isDemoMode() || isBrowserSessionDemo()) &&
        method === "GET" &&
        !shouldFetchPassthroughRealApi(url)
      ) {
        const mockResponse = getMockResponse(url);
        const resolved =
          mockResponse !== undefined
            ? mockResponse
            : usePlatformApi
              ? []
              : { count: 0, results: [] };

        if (!cancelled) {
          setData(resolved);
          setError(null);
          setLoading(false);
        }
        return true;
      }
      try {
        const response = await fetch(correct_url, options);
        if (cancelled) {
          return false;
        }
        if (response.status === 401) {
          authenticationService.logout();
          return false;
        }
        if (!response.ok) {
          if (!cancelled) {
            setError(new Error(`Request failed (${response.status})`));
            setLoading(false);
          }
          return false;
        }
        const json = await response.json();
        if (!cancelled) {
          // Paginated tenant lists use { count, results }; detail payloads (e.g. Spotify artist) do not.
          if (
            method === "GET" &&
            !usePlatformApi &&
            json &&
            typeof json === "object" &&
            Object.prototype.hasOwnProperty.call(json, "count") &&
            !Array.isArray(json.results)
          ) {
            setData({ ...json, count: json.count ?? 0, results: [] });
          } else {
            setData(json);
          }
          setLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          setError(error);
          setLoading(false);
        }
      }
    };

    fetchUrl();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange, url]);
  return [data, error, loading];
}
export { useFetch };